/**
 * @file twoFactorRules.ts
 * @brief Two-step verification (B31, ADR-059): the pure parts of the web
 *        client. Reads the 403 sign-in answers, keeps the "trust this device"
 *        token, and words the second-step errors. No imports, so
 *        `node --test` runs it as is.
 *
 * @details
 * - Every sign-in (password, Google, Apple, Microsoft, one-tap email link)
 *   can answer 403 TWO_FACTOR_REQUIRED (`twoFactor.pendingToken`, 10 min) or
 *   403 TWO_FACTOR_SETUP_REQUIRED (`twoFactor.setupToken`, 15 min).
 * - The pending and setup tokens live in memory only (never stored, never
 *   logged). The trusted-device token is kept in localStorage like the
 *   session (contract "Security notes for clients") and sent back as body
 *   `trustedDeviceToken` on every sign-in call.
 * - Codes may be typed with spaces ("123 456"); backup codes are
 *   case-insensitive with the dash optional. The server strips both.
 *
 * @references
 * - docs/product/two-step-verification.md (contract)
 * - BACKEND/src/features/auth/twoFactor.routes.mjs, twoFactor.service.mjs
 *
 * @author DES
 * @date 25-10-2026
 */

export type TwoFactorReason = "owner" | "superadmin" | "company";

/** What a sign-in answered instead of a session. */
export type TwoFactorChallenge =
  | {
      kind: "code";
      pendingToken: string;
      /** False for the Owner: "Trust this device" is not offered. */
      trustDeviceAllowed: boolean;
      /** Epoch ms when the pending token stops working. */
      expiresAt: number;
    }
  | {
      kind: "setup";
      setupToken: string;
      reason: TwoFactorReason | null;
      expiresAt: number;
    };

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

/** The challenge in a 403 sign-in answer, or null for any other error. */
export function challengeFromAnswer(
  status: number,
  code: string | undefined,
  body: unknown,
  now: number = Date.now(),
): TwoFactorChallenge | null {
  if (status !== 403) return null;
  const tf = obj(obj(body)?.twoFactor);
  if (!tf) return null;
  const ttl = typeof tf.expiresInSeconds === "number" && tf.expiresInSeconds > 0 ? tf.expiresInSeconds : 600;
  if (code === "TWO_FACTOR_REQUIRED" && typeof tf.pendingToken === "string" && tf.pendingToken) {
    return {
      kind: "code",
      pendingToken: tf.pendingToken,
      trustDeviceAllowed: tf.trustDeviceAllowed === true,
      expiresAt: now + ttl * 1000,
    };
  }
  if (code === "TWO_FACTOR_SETUP_REQUIRED" && typeof tf.setupToken === "string" && tf.setupToken) {
    const r = tf.reason;
    return {
      kind: "setup",
      setupToken: tf.setupToken,
      reason: r === "owner" || r === "superadmin" || r === "company" ? r : null,
      expiresAt: now + ttl * 1000,
    };
  }
  return null;
}

/** Codes that mean "this sign-in is over, start again". */
export function isTwoFactorRestart(code: string | undefined): boolean {
  return code === "TWO_FACTOR_TOKEN_INVALID";
}

/**
 * The line under the code box. The server's `message` is already plain
 * English; a wrong code adds how many tries are left.
 */
export function twoFactorErrorText(
  code: string | undefined,
  message: string | undefined,
  body: unknown,
): string {
  const b = obj(body);
  switch (code) {
    case "TWO_FACTOR_INVALID_CODE": {
      const left = typeof b?.attemptsLeft === "number" ? b.attemptsLeft : null;
      const base = "That code didn't work. Check it and try again.";
      if (left == null) return base;
      if (left <= 0) return "That code didn't work.";
      return `${base} ${left} ${left === 1 ? "try" : "tries"} left.`;
    }
    case "TWO_FACTOR_LOCKED": {
      const s = typeof b?.retryAfterSeconds === "number" ? b.retryAfterSeconds : 900;
      const m = Math.max(1, Math.ceil(s / 60));
      return `Too many wrong codes. Try again in ${m} ${m === 1 ? "minute" : "minutes"}.`;
    }
    case "TWO_FACTOR_TOKEN_INVALID":
      return "This sign-in has expired. Please sign in again.";
    case "TWO_FACTOR_CODE_MISSING":
    case "VALIDATION_ERROR":
      return "Enter the 6-digit code from your authenticator app.";
    case "TWO_FACTOR_SETUP_NOT_STARTED":
      return "The setup timed out. Start again to get a new QR code.";
    case "TWO_FACTOR_UNAVAILABLE":
      return "Two-step verification is not available right now. Please try again later.";
    case "RATE_LIMITED":
      return "Too many attempts. Please wait a few minutes and try again.";
    default:
      return message || "We couldn't check the code. Please try again.";
  }
}

/** Owner rules on admin + company routes (B31): the server's wording, with a fallback. */
export function ownerRuleText(code: string | undefined, message: string | undefined): string | null {
  if (code === "OWNER_PROTECTED") return message || "The Owner account can't be changed here.";
  if (code === "OWNER_ONLY") return message || "Only the Owner can do this.";
  return null;
}

/** "123 456" → "123456"; the 6 digits of an app code, or null. */
export function cleanAppCode(input: string): string | null {
  const d = input.replace(/[\s-]/g, "");
  return /^\d{6}$/.test(d) ? d : null;
}

/** A backup code as typed ("ABCDE FGHJK", "abcde-fghjk"); null if clearly not one. */
export function cleanBackupCode(input: string): string | null {
  const c = input.trim().replace(/\s+/g, "").toLowerCase();
  return /^[a-z0-9-]{8,32}$/.test(c) ? c : null;
}

// ----------------------------------------------------------------------
// Re-authentication for delete account / delete company (B33)
// ----------------------------------------------------------------------

/**
 * What a delete needs after 403 REAUTH_REQUIRED (Google / Apple / Microsoft
 * account signed in over 10 minutes ago): a code from the app when the
 * account has 2FA (`twoFactorAllowed`), else a fresh sign-in. Null for any
 * other answer.
 */
export function reauthKind(code: string | undefined, body: unknown): "code" | "signIn" | null {
  if (code !== "REAUTH_REQUIRED") return null;
  return obj(body)?.twoFactorAllowed === true ? "code" : "signIn";
}

/**
 * Answers to a delete sent with a code that belong under the code box: a
 * wrong code is 400 (not 401, so nothing signs out), the lock and the
 * limiter are 429. None of them is retried.
 */
export function isReauthCodeError(code: string | undefined): boolean {
  return (
    code === "TWO_FACTOR_INVALID_CODE" ||
    code === "TWO_FACTOR_LOCKED" ||
    code === "TWO_FACTOR_CODE_MISSING" ||
    code === "RATE_LIMITED"
  );
}

/** The delete body fields for a typed code, or null when it is not one. */
export function reauthProof(
  input: string,
  backup: boolean,
): { twoFactorCode: string } | { backupCode: string } | null {
  if (backup) {
    const c = cleanBackupCode(input);
    return c ? { backupCode: c } : null;
  }
  const c = cleanAppCode(input);
  return c ? { twoFactorCode: c } : null;
}

/** Why the account must set up 2FA, in one line for the setup-only screen. */
export function setupReasonText(reason: TwoFactorReason | null): string {
  switch (reason) {
    case "owner":
      return "The Owner account must use two-step verification.";
    case "superadmin":
      return "VERBLIZR staff accounts must use two-step verification.";
    case "company":
      return "Your company requires two-step verification for every member.";
    default:
      return "Your account needs two-step verification before you can sign in.";
  }
}

/** The text file "Download" saves. Plain text, one code per line. */
export function backupCodesFileText(codes: string[], accountName: string | null, now: Date = new Date()): string {
  const date = now.toISOString().slice(0, 10);
  return [
    "VERBLIZR backup codes",
    accountName ? `Account: ${accountName}` : null,
    `Made: ${date}`,
    "",
    "Each code works once, instead of a code from your authenticator app.",
    "Keep them somewhere safe, away from your phone.",
    "",
    ...codes,
    "",
  ]
    .filter((l): l is string => l !== null)
    .join("\n");
}

// ----------------------------------------------------------------------
// Trusted-device token ("Trust this device for 30 days")
// One per browser / Mac install: the token names its account, and the
// server ignores a token that is not the signing-in account's.
// ----------------------------------------------------------------------

export const TRUSTED_DEVICE_KEY = "verblizr.webpanel.2fa.trustedDevice";

type KeyValueStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function store(): KeyValueStore | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** The stored token while it is live, else null (an expired one is removed). */
export function readTrustedDevice(s: KeyValueStore | null = store(), now: number = Date.now()): string | null {
  if (!s) return null;
  try {
    const raw = s.getItem(TRUSTED_DEVICE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { token?: unknown; expiresAt?: unknown };
    const exp = typeof v.expiresAt === "string" ? Date.parse(v.expiresAt) : NaN;
    if (typeof v.token !== "string" || !v.token || (Number.isFinite(exp) && exp <= now)) {
      s.removeItem(TRUSTED_DEVICE_KEY);
      return null;
    }
    return v.token;
  } catch {
    return null;
  }
}

export function saveTrustedDevice(token: string, expiresAt: string | null, s: KeyValueStore | null = store()): void {
  try {
    s?.setItem(TRUSTED_DEVICE_KEY, JSON.stringify({ token, expiresAt }));
  } catch {
    // storage blocked: the code is asked again next time
  }
}

export function forgetTrustedDevice(s: KeyValueStore | null = store()): void {
  try {
    s?.removeItem(TRUSTED_DEVICE_KEY);
  } catch {
    // ignore
  }
}

/** The label the server lists the trusted device under. */
export function trustedDeviceLabel(userAgent: string, desktop: boolean): string {
  if (desktop) return "VERBLIZR Mac app";
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /Firefox\//.test(userAgent)
      ? "Firefox"
      : /Chrome\//.test(userAgent)
        ? "Chrome"
        : /Safari\//.test(userAgent)
          ? "Safari"
          : "Browser";
  const os = /Mac OS X|Macintosh/.test(userAgent)
    ? "Mac"
    : /Windows/.test(userAgent)
      ? "Windows"
      : /iPhone|iPad/.test(userAgent)
        ? "iPhone"
        : /Android/.test(userAgent)
          ? "Android"
          : /Linux/.test(userAgent)
            ? "Linux"
            : null;
  return os ? `${browser} on ${os}` : browser;
}
